import {
  FilterDocumentDurationFilter,
  FilterDocumentDurationTypes,
} from '@griply/types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, X } from 'shared/assets/icons';
import {
  CustomFormSelect,
  Size,
} from 'shared/components/ui/custom-form-select';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { ResizableInput } from 'shared/components/ui/resizable-input';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './filter-number-range.style';
import { formatNumberRangeDisplay } from './format-number-range-display';

export interface FilterNumberRangeProps {
  id: ID;
  label: string;
  minLabel: string;
  maxLabel: string;
  placeholder: string;
  moreValue?: number | null;
  lessValue?: number | null;
  moreType?: FilterDocumentDurationTypes;
  lessType?: FilterDocumentDurationTypes;
  onChange: (filter: FilterDocumentDurationFilter | null) => void;
  level?: number;
  compact?: boolean;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  canClear?: boolean;
}

export const FilterNumberRange: React.FC<FilterNumberRangeProps> = ({
  id,
  label,
  minLabel,
  maxLabel,
  placeholder,
  moreValue,
  lessValue,
  moreType,
  lessType,
  onChange,
  level,
  compact,
  icon,
  canClear,
}) => {
  const { t } = useTranslation();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [localMore, setLocalMore] = useState<string>('');
  const [localLess, setLocalLess] = useState<string>('');
  const [localMoreType, setLocalMoreType] =
    useState<FilterDocumentDurationTypes>(FilterDocumentDurationTypes.Minutes);
  const [localLessType, setLocalLessType] =
    useState<FilterDocumentDurationTypes>(FilterDocumentDurationTypes.Minutes);

  useEffect(() => {
    if (!menuOpen) {
      const more = moreValue ?? undefined;
      const less = lessValue ?? undefined;
      setLocalMore(more?.toString() ?? '');
      setLocalLess(less?.toString() ?? '');
      setLocalMoreType(moreType ?? FilterDocumentDurationTypes.Minutes);
      setLocalLessType(lessType ?? FilterDocumentDurationTypes.Minutes);
    }
  }, [menuOpen, moreValue, lessValue, moreType, lessType]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
    closeMenu();
  };

  const handleClose = () => {
    const lessValue = parseFloat(localLess);
    const moreValue = parseFloat(localMore);

    onChange(
      !isNumber(lessValue) && !isNumber(moreValue)
        ? null
        : {
            more: isNumber(moreValue)
              ? { type: localMoreType, value: moreValue }
              : null,
            less: isNumber(lessValue)
              ? { type: localLessType, value: lessValue }
              : null,
          },
    );

    closeMenu();
  };

  const lessTypeOptions = useMemo(
    () => [
      {
        value: FilterDocumentDurationTypes.Minutes,
        label: t('general.labels.time.minutes', {
          count: parseFloat(localLess),
        }),
      },
      {
        value: FilterDocumentDurationTypes.Hours,
        label: t('general.labels.time.hours', { count: parseFloat(localLess) }),
      },
    ],
    [localLess, t],
  );
  const moreTypeOptions = useMemo(
    () => [
      {
        value: FilterDocumentDurationTypes.Minutes,
        label: t('general.labels.time.minutes', {
          count: parseFloat(localMore),
        }),
      },
      {
        value: FilterDocumentDurationTypes.Hours,
        label: t('general.labels.time.hours', { count: parseFloat(localMore) }),
      },
    ],
    [localMore, t],
  );

  const displayText = formatNumberRangeDisplay(
    placeholder,
    moreValue,
    lessValue,
  );

  const hasValue = isNumber(moreValue) || isNumber(lessValue);
  const showClearButton = hasValue && canClear;

  return (
    <>
      <Styled.Container htmlFor={id} $level={level} $compact={!!compact}>
        {!!icon && (
          <Styled.AffixContainer>
            <Icon icon={icon} />
          </Styled.AffixContainer>
        )}

        <Styled.Content>
          {label}

          <Styled.ButtonContainer ref={containerRef}>
            <Styled.Button
              $isPlaceholder={!hasValue}
              onClick={openMenu}
              type="button"
              id={id}
            >
              {displayText}
            </Styled.Button>

            {showClearButton ? (
              <Styled.SelectIconContainer onClick={handleClear}>
                <Icon icon={X} />
              </Styled.SelectIconContainer>
            ) : (
              <Styled.SelectIconContainer onClick={openMenu}>
                <Icon icon={ChevronLeft} />
              </Styled.SelectIconContainer>
            )}
          </Styled.ButtonContainer>
        </Styled.Content>
      </Styled.Container>

      {menuOpen && (
        <PopupMenu
          ref={menuRef}
          referenceElement={containerRef}
          onOutside={handleClose}
        >
          <PopupMenuList>
            <PopupMenuListItem>
              <Styled.ItemContainer>
                <Styled.Label htmlFor="min">{minLabel}</Styled.Label>

                <Styled.InputsContainer>
                  <ResizableInput
                    id="min"
                    value={localMore}
                    type="number"
                    placeholder="90"
                    onChange={(e) => setLocalMore(e.target.value)}
                    min={0}
                    step={1}
                    hideNumberButtons
                    showAsSmallFormInput
                  />
                  <CustomFormSelect
                    size={Size.ExtraSmall}
                    options={moreTypeOptions}
                    value={localMoreType}
                    onChange={(type) =>
                      setLocalMoreType(type as FilterDocumentDurationTypes)
                    }
                  />
                </Styled.InputsContainer>
              </Styled.ItemContainer>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <Styled.ItemContainer>
                <Styled.Label htmlFor="max">{maxLabel}</Styled.Label>

                <Styled.InputsContainer>
                  <ResizableInput
                    id="max"
                    value={localLess}
                    type="number"
                    placeholder="120"
                    onChange={(e) => setLocalLess(e.target.value)}
                    min={0}
                    step={1}
                    hideNumberButtons
                    showAsSmallFormInput
                  />
                  <CustomFormSelect
                    size={Size.ExtraSmall}
                    options={lessTypeOptions}
                    value={localLessType}
                    onChange={(type) =>
                      setLocalLessType(type as FilterDocumentDurationTypes)
                    }
                  />
                </Styled.InputsContainer>
              </Styled.ItemContainer>
            </PopupMenuListItem>
          </PopupMenuList>

          {/*{showClearButton && (*/}
          {/*  <PopupMenuList>*/}
          {/*    <PopupMenuListItem>*/}
          {/*      <PopupMenuButton*/}
          {/*        onClick={handleClear}*/}
          {/*        variant={ButtonVariants.Error}*/}
          {/*      >*/}
          {/*        {t('context-menus.filtering.task.filters.clear.label')}*/}
          {/*      </PopupMenuButton>*/}
          {/*    </PopupMenuListItem>*/}
          {/*  </PopupMenuList>*/}
          {/*)}*/}
        </PopupMenu>
      )}
    </>
  );
};
